import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Copy, Globe, Loader2, Shield, Trash2, UserPlus, X } from 'lucide-react'

import {
  addShare, createPublicLink, listShares, revokePublicLink, revokeShare,
  updateDocument, type DocumentShare, type DocumentSummary, type SharePermission,
  type SharePrincipalType, type SharingMode,
} from '@/api/documents'

interface Props {
  document: DocumentSummary
  onClose: () => void
  onChange: () => void
}

const ROLE_OPTIONS = ['ENTERPRISE_ADMIN', 'PROJECT_OWNER', 'DEVELOPER']

/**
 * Share dialog — owner-only. Lets the document owner pick:
 *  - Sharing mode (private / tenant / users / roles / public_link)
 *  - Add specific users or roles with view/edit permission
 *  - Generate a public link with view/edit permission
 *
 * The dialog mirrors Notion's per-page share menu: sharing mode is the
 * coarse default, with explicit shares acting as additive grants.
 */
export default function ShareDialog({ document, onClose, onChange }: Props) {
  const [shares, setShares] = useState<DocumentShare[]>([])
  const [sharingMode, setSharingMode] = useState<SharingMode>(document.sharing_mode)
  const [loading, setLoading] = useState(true)

  const [principalType, setPrincipalType] = useState<SharePrincipalType>('user')
  const [principalId, setPrincipalId] = useState('')
  const [permission, setPermission] = useState<SharePermission>('view')
  const [adding, setAdding] = useState(false)

  const [linkBusy, setLinkBusy] = useState(false)
  const [activeLink, setActiveLink] = useState<{ token: string; permission: SharePermission } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listShares(document.id)
      setShares(res.shares)
      setSharingMode(res.sharing_mode)
    } catch {
      toast.error('Failed to load sharing settings')
    } finally {
      setLoading(false)
    }
  }, [document.id])

  useEffect(() => { load() }, [load])

  const handleModeChange = async (mode: SharingMode) => {
    try {
      await updateDocument(document.id, { sharing_mode: mode })
      setSharingMode(mode)
      toast.success('Sharing updated')
      onChange()
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to update sharing'
      toast.error(msg)
    }
  }

  const handleAddShare = async () => {
    if (!principalId.trim()) {
      toast.error(principalType === 'user' ? 'Enter a user id or email' : 'Pick a role')
      return
    }
    setAdding(true)
    try {
      await addShare(document.id, {
        principal_type: principalType,
        principal_id: principalId.trim(),
        permission,
      })
      setPrincipalId('')
      toast.success('Access granted')
      load()
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to grant access'
      toast.error(msg)
    } finally {
      setAdding(false)
    }
  }

  const handleRevoke = async (id: number) => {
    try {
      await revokeShare(document.id, id)
      load()
    } catch {
      toast.error('Failed to revoke')
    }
  }

  const handleCreateLink = async (perm: SharePermission) => {
    setLinkBusy(true)
    try {
      const link = await createPublicLink(document.id, perm)
      setActiveLink({ token: link.token, permission: link.permission })
      toast.success('Public link created')
      load()
    } catch {
      toast.error('Failed to create public link')
    } finally {
      setLinkBusy(false)
    }
  }

  const handleRevokeLink = async () => {
    setLinkBusy(true)
    try {
      await revokePublicLink(document.id)
      setActiveLink(null)
      toast.success('Public link revoked')
      load()
    } catch {
      toast.error('Failed to revoke link')
    } finally {
      setLinkBusy(false)
    }
  }

  const copyLink = (token: string) => {
    const runtimeBase = (window as any).__TWASTA_RUNTIME_URL__ || '/runtime'
    const url = `${window.location.origin}${runtimeBase}/documents/public/${token}`
    navigator.clipboard.writeText(url)
    toast.success('Link copied')
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
          borderRadius: 8, padding: 18, width: 540, maxHeight: '85vh', overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Share · {document.title}</h3>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm"><X size={13} /></button>
        </div>

        {/* Sharing mode */}
        <section style={{ marginTop: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-secondary)' }}>
            Default access
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginTop: 6 }}>
            {(['private', 'tenant', 'users', 'roles', 'public_link'] as SharingMode[]).map(m => (
              <button
                key={m}
                type="button"
                onClick={() => handleModeChange(m)}
                style={{
                  padding: '8px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                  border: '1px solid ' + (sharingMode === m ? 'var(--color-accent)' : 'var(--color-border)'),
                  background: sharingMode === m ? 'var(--color-accent)' : 'var(--color-bg)',
                  color: sharingMode === m ? 'var(--color-on-accent, #fff)' : 'var(--color-text)',
                  cursor: 'pointer',
                }}
              >
                {m === 'private' ? 'Private (only invited)' :
                 m === 'tenant' ? 'Everyone in tenant' :
                 m === 'users' ? 'Selected users' :
                 m === 'roles' ? 'Selected roles' :
                 'Public link'}
              </button>
            ))}
          </div>
        </section>

        {/* Add share */}
        <section style={{ marginTop: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-secondary)' }}>
            Grant access
          </label>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <select value={principalType} onChange={(e) => { setPrincipalType(e.target.value as SharePrincipalType); setPrincipalId('') }} style={{
              fontSize: 14, padding: '6px 8px', borderRadius: 6,
              border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
            }}>
              <option value="user">User</option>
              <option value="role">Role</option>
            </select>
            {principalType === 'user' ? (
              <input
                value={principalId}
                onChange={(e) => setPrincipalId(e.target.value)}
                placeholder="user_id"
                style={{
                  flex: 1, fontSize: 14, padding: '6px 8px', borderRadius: 6,
                  border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
                }}
              />
            ) : (
              <select value={principalId} onChange={(e) => setPrincipalId(e.target.value)} style={{
                flex: 1, fontSize: 14, padding: '6px 8px', borderRadius: 6,
                border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
              }}>
                <option value="">— Select role —</option>
                {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
            <select value={permission} onChange={(e) => setPermission(e.target.value as SharePermission)} style={{
              fontSize: 14, padding: '6px 8px', borderRadius: 6,
              border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
            }}>
              <option value="view">Can view</option>
              <option value="edit">Can edit</option>
            </select>
            <button type="button" onClick={handleAddShare} disabled={adding} className="btn btn-primary btn-sm">
              {adding ? <Loader2 size={13} className="spin" /> : <UserPlus size={13} />} Add
            </button>
          </div>

          {/* Existing shares list */}
          <div style={{ marginTop: 10 }}>
            {loading ? (
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading…</div>
            ) : shares.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>No explicit shares yet.</div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
                {shares.map(s => (
                  <li key={s.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6,
                    fontSize: 13,
                  }}>
                    <span>
                      <Shield size={11} style={{ marginRight: 4, opacity: 0.6 }} />
                      <strong>{s.principal_type}</strong> · {s.principal_id} · {s.permission}
                    </span>
                    <button type="button" onClick={() => handleRevoke(s.id)} className="btn btn-ghost btn-sm" title="Revoke">
                      <Trash2 size={11} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Public link */}
        <section style={{ marginTop: 16, padding: 12, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-bg)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Globe size={13} style={{ opacity: 0.6 }} />
            <strong style={{ fontSize: 13 }}>Public link</strong>
          </div>
          {activeLink ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                Permission: <strong>{activeLink.permission}</strong>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={() => copyLink(activeLink.token)} className="btn btn-ghost btn-sm">
                  <Copy size={13} /> Copy link
                </button>
                <button type="button" onClick={handleRevokeLink} disabled={linkBusy} className="btn btn-ghost btn-sm" style={{ color: 'var(--color-error)' }}>
                  <Trash2 size={13} /> Revoke
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => handleCreateLink('view')} disabled={linkBusy} className="btn btn-ghost btn-sm">
                Create view link
              </button>
              <button type="button" onClick={() => handleCreateLink('edit')} disabled={linkBusy} className="btn btn-ghost btn-sm">
                Create edit link
              </button>
            </div>
          )}
          <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
            Anyone with the link can {activeLink?.permission === 'edit' ? 'edit' : 'view'} this document. Revoke any time.
          </p>
        </section>
      </div>
    </div>
  )
}
